import { z } from 'zod';

export const AnnouncementAudienceSchema = z.enum(['ALL', 'ADMIN_HR', 'MANAGERS']);

/**
 * Severity, kept to two values on purpose. `audience` says who sees it and
 * `pinned` says where it sits; neither says how much it matters. URGENT is the
 * only level that changes behaviour (accent colour, and the wording of the
 * notification recipients get), so a finer taxonomy would only be decoration
 * the author has to guess at.
 */
export const AnnouncementPrioritySchema = z.enum(['NORMAL', 'URGENT']);

/**
 * WHICH PART OF THE WORKFORCE, which is a different question from `audience`.
 *
 * `audience` is about ROLE — what a reader is allowed to administer. This is
 * about WHO SOMEBODY IS, and it is the same split every other screen in the app
 * offers (`apps/web/src/lib/rosterScope.ts`): two `Employee.workforceType`
 * values plus REMOTE, which is not a workforce type at all but an
 * `attendanceProfile` of SELF_ANYWHERE — how somebody punches, not what they
 * are. The words and their order are that module's; only the storage is here.
 *
 * A remote office worker is OFFICE_STAFF *and* REMOTE, so these never partition
 * the roster and a reader can be matched by more than one of them. That is why
 * the column holds ONE value and the reader is tested for membership, rather
 * than the reverse.
 */
export const AnnouncementWorkforceGroupSchema = z.enum([
  'SITE_WORKER',
  'OFFICE_STAFF',
  'REMOTE',
]);

export const AnnouncementInputSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(10_000),
  audience: AnnouncementAudienceSchema.default('ALL'),
  priority: AnnouncementPrioritySchema.default('NORMAL'),
  pinned: z.boolean().default(false),
  publishedAt: z.string().datetime().optional(),
  expiresAt: z.string().datetime().optional().nullable(),
  /*
   * TWO NARROWERS, BOTH NULLABLE, AND `null` IS THE ONLY WAY TO SAY "EVERYONE".
   *
   * They ADD to `audience` rather than replacing it — see the composition note
   * in `announcement.service.ts`. `.default(null)` and not `.optional()`: this
   * schema is also what `announcements.routes.ts` re-parses a PATCH through, and
   * an optional key that vanishes is a key `service.update()` would write as
   * `undefined`. Every announcement written before this feature existed carries
   * NULL in both, which is exactly "not narrowed" — no backfill, no behaviour
   * change, and no row can GAIN a reader it did not already have.
   */
  departmentId: z.string().min(1).nullable().default(null),
  workforceGroup: AnnouncementWorkforceGroupSchema.nullable().default(null),
  /*
   * NAMED PEOPLE — the fourth narrower, and the only one that is a SET.
   *
   * `[]` and not `null` is its spelling of "not narrowed", because a set already
   * has an empty value and a nullable array would give the same fact two
   * spellings. `.default([])` for the same reason the two above use
   * `.default(null)`: `announcements.routes.ts` re-parses a PATCH through this
   * schema, and an optional key that vanishes is a key `service.update()` would
   * write as `undefined`.
   *
   * These employee ids INTERSECT with everything above them: a named person
   * receives the notice only if they ALSO satisfy the role audience, the
   * department and the workforce group. So this cannot widen a row's reach
   * either — the invariant every narrower here keeps.
   *
   * NOT VALIDATED HERE beyond "these are ids". Whether an id belongs to an
   * employee who HAS A LOGIN is a database question, and the service answers it
   * on every write (`announcement.service.ts`): an employee with no `User` has
   * no notification bell, so an id naming one is dropped rather than stored.
   */
  recipientIds: z.array(z.string().min(1)).max(500).default([]),
  /*
   * THE GATE NOTICEBOARD — and these two are NOT a fifth and sixth narrower.
   *
   * Everything above addresses a PERSON. These address a CHANNEL: the Android
   * panels at site and camp gates, which have a worksite and whoever happens to
   * be standing in front of them. `fieldAnnouncements.ts` holds the rule and the
   * reasoning; the part that matters here is that it applies the four narrowers
   * above FIRST, and only a notice narrowed to nobody may reach a board at all.
   *
   * `onGateBoards: false` is "internal only" — keep this all-company notice off
   * those screens. `.default(true)` and not `.optional()`, for the same reason
   * the two nullable columns above use `.default(null)`: this schema is what
   * `announcements.routes.ts` re-parses a PATCH through, and an optional key
   * that vanishes is a key `service.update()` would write as `undefined`. TRUE
   * is what every row written before this column existed carries, so no stored
   * announcement changes which boards it reaches.
   *
   * `gateBoardWorksiteId` pins the notice to ONE site's boards; `null` is
   * "every board it is already entitled to". ONE and not a set, because at any
   * one gate a notice pinned to several sites is indistinguishable from one
   * pinned to that gate's own site — see the migration for the whole argument.
   *
   * NEITHER CAN WIDEN. `false` removes every board; a worksite removes every
   * board that is not at it. And neither can rescue a notice the person-level
   * targeting excluded: pinning a departmental notice to Site A narrows an
   * empty set to an empty set, because a gate screen cannot tell whether the
   * person reading it is in that department.
   */
  onGateBoards: z.boolean().default(true),
  gateBoardWorksiteId: z.string().min(1).nullable().default(null),
});
export type AnnouncementInput = z.infer<typeof AnnouncementInputSchema>;
export type AnnouncementPriority = z.infer<typeof AnnouncementPrioritySchema>;
export type AnnouncementAudience = z.infer<typeof AnnouncementAudienceSchema>;
export type AnnouncementWorkforceGroup = z.infer<typeof AnnouncementWorkforceGroupSchema>;
